import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useParams } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { format, parseISO, isValid } from 'date-fns';

function toISO(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

export default function UserMoodAndDailyThoughts() {
  const { id = '' } = useParams();
  const { getParams } = useModifiableParams({
    defaults: { timeframe: 'week' },
  });
  const params = getParams();

  const today = new Date();

  let startDate: string | undefined = params.startDate;
  let endDate: string | undefined = params.endDate;

  if (!startDate || !endDate) {
    if (params.timeframe === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = toISO(first);
      endDate = toISO(today);
    } else {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      startDate = toISO(start);
      endDate = toISO(today);
    }
  }

  const topMoodsApi =
    Api.MetricActivity.useReadCollectionTopMoodsStartDateEndDate(
      id,
      startDate,
      endDate,
    );

  const activitiesApi = Api.MetricActivity.useReadCollectionMetricActivities(
    id,
    {
      'date[after]': startDate,
      'date[before]': endDate,
    },
  );

  console.log(activitiesApi.data);

  const topMoods = (topMoodsApi.data?.member || []).map((m) => ({
    mood: m.mood.title,
    count: m.count,
  }));

  const moodLogs = (activitiesApi.data?.member || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .map((a) => {
      const d = parseISO(a.date);
      const dateLabel = isValid(d) ? format(d, 'EEE, d MMM yyyy') : a.date;
      const loggedMoods = (a.mood || []).map((m) => m.title).join(', ') || '-';
      const dailyThought = (a.thought && a.thought.trim()) || '-';
      return { date: dateLabel, loggedMoods, dailyThought };
    });

  return (
    <Stack spacing={3}>
      <Card>
        <CardHeader title={<Typography variant="h6">Top Moods</Typography>} />
        <CardContent sx={{ pt: 0 }}>
          <Stack spacing={1}>
            {topMoods.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" sx={{ minWidth: '120px' }}>
                  {item.mood}
                </Typography>
                <Typography variant="body1">{item.count} Times</Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={
            <Typography variant="h6">
              Complete Mood Logs & Daily Thoughts
            </Typography>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '20%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">Date</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '30%' }}>Logged Moods</TableCell>
                  <TableCell sx={{ width: '50%' }}>Daily Thought</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {moodLogs.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2">{row.date}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.loggedMoods}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {row.dailyThought}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
}
